"use client";

import { Paperclip, X } from "lucide-react";
import { useTranslations } from "next-intl";
import { useRef, useState } from "react";
import { Button, type ButtonProps } from "@/components/ui/button";
import { useDeleteElectionAttachment } from "@/hooks/dao/election/useDeleteElectionAttachment";
import { useUploadElectionAttachment } from "@/hooks/dao/election/useUploadElectionAttachment";

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5 MB per OI-1
const MAX_FILE_COUNT = 1; // r4 — single attachment retained at any time
const ALLOWED_MIME_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);

type Phase = "empty" | "uploading" | "uploaded";

interface AttachmentUploaderProps {
  onChange: (urls: string[]) => void;
  /**
   * Fires whenever the uploader transitions in/out of the "uploading" phase.
   * The candidacy form uses this to disable submit while a file is in flight —
   * otherwise users who click submit mid-upload create an election row without
   * the attachment URL, and the file ends up orphaned in object storage.
   */
  onUploadingChange?: (uploading: boolean) => void;
  /** Override the row label (default: the election attachments label). */
  label?: string;
  /** Override the upload button colour (default: "secondary"). */
  uploadButtonColors?: ButtonProps["colors"];
}

/**
 * Single-file picker + upload UI for election candidacy attachments.
 * 3-state machine: empty | uploading | uploaded.
 *
 * r4 (S3) spec:
 * - MAX_FILE_COUNT = 1 (was 2)
 * - XHR upload via useUploadElectionAttachment with onProgress callback
 * - X-button delete via useDeleteElectionAttachment
 * - 403 DELETE response silently clears state (pre-PATCH orphan window)
 * - No change-file branch in handlePick (PM lock #4)
 * - RTL: first JSX child = label+filename (renders RIGHT), last = action button (renders LEFT)
 */
export const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  onChange,
  onUploadingChange,
  label,
  uploadButtonColors = "secondary",
}) => {
  const t = useTranslations("dao");
  const [phase, setPhase] = useState<Phase>("empty");
  const [filename, setFilename] = useState<string | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const { uploadAsync } = useUploadElectionAttachment();
  const { deleteAsync } = useDeleteElectionAttachment();

  function setPhaseAndNotify(next: Phase) {
    setPhase(next);
    onUploadingChange?.(next === "uploading");
  }

  function openPicker() {
    inputRef.current?.click();
  }

  async function runUpload(file: File) {
    // Client-side validation
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setError(t("election.attachments.tooLarge"));
      setPhaseAndNotify("empty");
      return;
    }
    if (!ALLOWED_MIME_TYPES.has(file.type)) {
      setError(t("election.attachments.invalidType"));
      setPhaseAndNotify("empty");
      return;
    }

    setPhaseAndNotify("uploading");
    setFilename(file.name);
    setProgress(0);
    setError(null);

    try {
      const url = await uploadAsync(file, (pct) => setProgress(pct));
      setUploadedUrl(url);
      setPhaseAndNotify("uploaded");
      setProgress(100);
      onChange([url]);
    } catch {
      setPhaseAndNotify("empty");
      setUploadedUrl(null);
      setFilename(null);
      setProgress(0);
      setError(t("election.attachments.uploadFailed"));
      onChange([]);
    }
  }

  async function handlePick(e: React.ChangeEvent<HTMLInputElement>) {
    // MAX_FILE_COUNT enforcement — constant is the runtime source of truth
    const uploadedCount = uploadedUrl !== null ? 1 : 0;
    if (uploadedCount >= MAX_FILE_COUNT) return;
    const file = e.target.files?.[0];
    if (!file) return;
    await runUpload(file);
    e.target.value = ""; // allow same-file reselect after future X-click
  }

  async function handleDelete() {
    if (phase !== "uploaded" || !uploadedUrl) return;

    try {
      await deleteAsync(uploadedUrl);
      // 204 — clean success
      setUploadedUrl(null);
      setFilename(null);
      setProgress(0);
      setPhaseAndNotify("empty");
      setError(null);
      onChange([]);
    } catch (err: unknown) {
      // PM lock #3: 403 orphan pre-PATCH window → swallow silently + clear state
      const status =
        err !== null && typeof err === "object"
          ? Reflect.get(err, "status")
          : undefined;

      if (status === 403) {
        setUploadedUrl(null);
        setFilename(null);
        setProgress(0);
        setPhaseAndNotify("empty");
        setError(null);
        onChange([]);
        return;
      }

      // Other failures (400, 500) → show error, stay in uploaded
      setError(t("election.attachments.deleteFailed"));
    }
  }

  return (
    <div className="flex flex-col gap-2">
      {/* Row: FIRST child = visual RIGHT (label + filename), LAST child = visual LEFT (action) */}
      <div className="flex items-center justify-between gap-3">
        {/* FIRST CHILD — renders on the RIGHT in RTL */}
        <div className="flex items-center gap-2 min-w-0">
          <Paperclip className="w-4 h-4 shrink-0" aria-label="paperclip" />
          <span className="text-sm font-medium text-text-primary shrink-0">
            {label ?? t("election.attachments.label")}
          </span>
          {phase !== "empty" && filename && (
            <span
              className="text-xs text-text-secondary truncate max-w-[160px]"
              title={filename}
            >
              {filename}
            </span>
          )}
        </div>

        {/* LAST CHILD — renders on the LEFT in RTL */}
        {phase === "empty" && (
          <Button
            type="button"
            variant="solid"
            colors={uploadButtonColors}
            onClick={openPicker}
            aria-label={t("election.attachments.uploadAria")}
          >
            {t("election.attachments.upload")}
          </Button>
        )}
        {phase === "uploaded" && (
          <Button
            type="button"
            variant="faded"
            colors="secondary"
            onClick={() => void handleDelete()}
            aria-label={t("election.attachments.deleteAria")}
          >
            <X size={16} />
          </Button>
        )}
        {phase === "uploading" && (
          <span className="text-xs text-text-secondary">{progress}%</span>
        )}
      </div>

      {/* Progress bar — visible only during upload */}
      {phase === "uploading" && (
        <div className="w-full h-1 bg-bg-tertiary rounded-full overflow-hidden">
          <div
            className="h-full bg-text-primary transition-[width] duration-150"
            style={{ width: `${progress}%` }}
          />
        </div>
      )}

      {/* Inline error */}
      {error && <p className="text-xs text-semantic-error">{error}</p>}

      {/* Hidden file input — single file (no multiple attr) */}
      <input
        ref={inputRef}
        type="file"
        accept=".jpg,.jpeg,.png,.webp,.pdf,image/jpeg,image/png,image/webp,application/pdf"
        className="hidden"
        onChange={handlePick}
        aria-label={t("election.attachments.pickAria")}
      />
    </div>
  );
};
